import axios from "axios";
import type {
  AuthenticationResponseJSON,
  PublicKeyCredentialCreationOptionsJSON,
  PublicKeyCredentialRequestOptionsJSON,
  RegistrationResponseJSON,
} from "@simplewebauthn/browser";
import { authUrl, baseUrl } from "../config/constants";

// Passkeys (RM-3845). owl decides whether they're on (PASSKEYS_ENABLED), so
// barn-owl asks rather than shipping its own flag.

export interface PasskeyStatus {
  enabled: boolean;
  // false during the staff-only rollout: only browsers that already used a
  // passkey show the button.
  show_sign_in_button: boolean;
}

export interface Passkey {
  id: number;
  credentialId: string;
  nickname: string;
  backedUp: boolean;
  createdAt: string;
  lastUsedAt: string | null;
}

export interface MyPasskeys {
  available: boolean;
  rp_id: string;
  passkeys: Passkey[];
}

interface Ceremony<T> {
  challenge_id: string;
  options: T;
}

export default {
  getStatus: async (): Promise<PasskeyStatus> => {
    const res = await axios.get(`${authUrl}/passkey`);
    return res.data;
  },
  signInOptions: async (): Promise<
    Ceremony<PublicKeyCredentialRequestOptionsJSON>
  > => {
    const res = await axios.post(`${authUrl}/passkey/options`);
    return res.data;
  },
  // Answers like a password sign-in: session headers plus { data: user }.
  verifySignIn: async (
    challengeId: string,
    credential: AuthenticationResponseJSON,
  ) => {
    return axios.post(`${authUrl}/passkey/verify`, {
      challenge_id: challengeId,
      credential,
    });
  },

  getMine: async (): Promise<MyPasskeys> => {
    const res = await axios.get(`${baseUrl}/my/passkeys`);
    return res.data;
  },
  registrationOptions: async (
    currentPassword: string,
  ): Promise<Ceremony<PublicKeyCredentialCreationOptionsJSON>> => {
    const res = await axios.post(`${baseUrl}/my/passkeys/options`, {
      current_password: currentPassword,
    });
    return res.data;
  },
  create: async (
    challengeId: string,
    credential: RegistrationResponseJSON,
    nickname: string,
  ): Promise<Passkey> => {
    const res = await axios.post(`${baseUrl}/my/passkeys`, {
      challenge_id: challengeId,
      credential,
      nickname,
    });
    return res.data.passkey;
  },
  rename: async (id: number, nickname: string): Promise<Passkey> => {
    const res = await axios.put(`${baseUrl}/my/passkeys/${id}`, { nickname });
    return res.data.passkey;
  },
  remove: async (
    id: number,
    currentPassword: string,
  ): Promise<{ credential_id: string; rp_id: string }> => {
    const res = await axios.delete(`${baseUrl}/my/passkeys/${id}`, {
      data: { current_password: currentPassword },
    });
    return res.data;
  },
};
